import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import Modal from '../../../shared/components/Modal';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import './PeriodsPage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE_URL = baseEnvironment.replace(/\/$/, '');
const PERIOD_TYPES = ['MONTHLY', 'QUARTERLY', 'SEMI_ANNUAL', 'YEARLY', 'CUSTOM'];
const STATUS_OPTIONS = ['OPEN', 'LOCKED', 'CLOSED'];

const UI_TEXT = {
  'tr-TR': {
    pageTitle: 'Donemler',
    pageDescription: 'Finansal raporlama donemlerini yonet.',
    newPeriod: 'Yeni donem',
    companiesLoadFailed: 'Sirketler yuklenemedi.',
    companiesLoadFailedDetail: 'Sirketler yuklenirken hata olustu.',
    periodsLoadFailed: 'Donemler yuklenemedi.',
    periodsLoadFailedDetail: 'Donemler yuklenirken hata olustu.',
    requiredFields: 'Sirket, donem tipi ve tarih araligi zorunludur.',
    invalidDateRange: 'Baslangic tarihi bitis tarihinden once olmalidir.',
    operationFailed: 'Islem basarisiz oldu.',
    periodUpdated: 'Donem guncellendi.',
    periodCreated: 'Donem tanimlandi.',
    operationFailedDetail: 'Islem sirasinda hata olustu.',
    deleteConfirm: 'Bu donemi silmek istediginizden emin misiniz?',
    deleteFailed: 'Silme islemi basarisiz oldu.',
    periodDeleted: 'Donem silindi.',
    deleteFailedDetail: 'Silme sirasinda hata olustu.',
    company: 'Sirket',
    companyNotFound: 'Sirket bulunamadi',
    code: 'Kod',
    label: 'Etiket',
    type: 'Tip',
    start: 'Baslangic',
    end: 'Bitis',
    status: 'Durum',
    action: 'Aksiyon',
    loading: 'Yukleniyor...',
    noRecords: 'Henuz donem tanimi bulunamadi.',
    edit: 'Duzenle',
    delete: 'Sil',
    editPeriodAria: 'Donemi duzenle',
    newPeriodAria: 'Yeni donem ekle',
    editPeriodTitle: 'Donemi duzenle',
    newPeriodTitle: 'Yeni donem ekle',
    close: 'Kapat',
    periodType: 'Donem tipi',
    startDate: 'Baslangic tarihi',
    endDate: 'Bitis tarihi',
    periodLabel: 'Label',
    periodLabelPlaceholder: 'Donem etiketi',
    codeGenerated: 'Kod (sistem uretimi)',
    saving: 'Kaydediliyor...',
    update: 'Guncelle',
    save: 'Kaydet',
    cancel: 'Iptal',
    statusOpen: 'Acik',
    statusLocked: 'Kilitli',
    statusClosed: 'Kapali',
    periodMonthly: 'Aylik',
    periodQuarterly: 'Ceyreklik',
    periodSemiAnnual: 'Alti Aylik',
    periodYearly: 'Yillik',
    periodCustom: 'Ozel'
  },
  'en-US': {
    pageTitle: 'Periods',
    pageDescription: 'Manage financial reporting periods.',
    newPeriod: 'New period',
    companiesLoadFailed: 'Companies could not be loaded.',
    companiesLoadFailedDetail: 'An error occurred while loading companies.',
    periodsLoadFailed: 'Periods could not be loaded.',
    periodsLoadFailedDetail: 'An error occurred while loading periods.',
    requiredFields: 'Company, period type, and date range are required.',
    invalidDateRange: 'Start date must be earlier than end date.',
    operationFailed: 'Operation failed.',
    periodUpdated: 'Period updated.',
    periodCreated: 'Period created.',
    operationFailedDetail: 'An error occurred during the operation.',
    deleteConfirm: 'Are you sure you want to delete this period?',
    deleteFailed: 'Delete operation failed.',
    periodDeleted: 'Period deleted.',
    deleteFailedDetail: 'An error occurred during delete.',
    company: 'Company',
    companyNotFound: 'Company not found',
    code: 'Code',
    label: 'Label',
    type: 'Type',
    start: 'Start',
    end: 'End',
    status: 'Status',
    action: 'Action',
    loading: 'Loading...',
    noRecords: 'No period definition found yet.',
    edit: 'Edit',
    delete: 'Delete',
    editPeriodAria: 'Edit period',
    newPeriodAria: 'Add new period',
    editPeriodTitle: 'Edit period',
    newPeriodTitle: 'Add new period',
    close: 'Close',
    periodType: 'Period type',
    startDate: 'Start date',
    endDate: 'End date',
    periodLabel: 'Label',
    periodLabelPlaceholder: 'Period label',
    codeGenerated: 'Code (system generated)',
    saving: 'Saving...',
    update: 'Update',
    save: 'Save',
    cancel: 'Cancel',
    statusOpen: 'Open',
    statusLocked: 'Locked',
    statusClosed: 'Closed',
    periodMonthly: 'Monthly',
    periodQuarterly: 'Quarterly',
    periodSemiAnnual: 'Semi-Annual',
    periodYearly: 'Yearly',
    periodCustom: 'Custom'
  }
};

const createFormState = (companyId = '') => ({
  id: null,
  companyId,
  periodType: 'YEARLY',
  label: '',
  startDate: '',
  endDate: '',
  status: 'OPEN'
});

const formatDisplayDate = (value, locale = 'tr-TR') => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return date.toLocaleDateString(locale);
  }
  const [year, month, day] = value.split('-');
  return day ? `${day}.${month}.${year}` : value;
};

const computeCodePreview = (periodType, startDate, endDate) => {
  if (!periodType || !startDate || !endDate) {
    return '';
  }
  const start = startDate.replace(/-/g, '');
  const end = endDate.replace(/-/g, '');
  return `P-${periodType}-${start}-${end}`.toUpperCase();
};

const computeDefaultLabel = (periodType, startDate, endDate) => {
  if (!startDate || !endDate) {
    return '';
  }
  const [startYear] = startDate.split('-');
  const [endYear] = endDate.split('-');
  if (periodType === 'YEARLY' && startYear && endYear && startYear === endYear) {
    return startYear;
  }
  return `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`;
};

function PeriodsPage() {
  const { language } = useLanguage();
  const text = UI_TEXT[language] || UI_TEXT['tr-TR'];
  const t = (key) => text[key] || UI_TEXT['tr-TR'][key] || key;

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');
  const [formState, setFormState] = useState(createFormState());
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isEditMode = Boolean(formState.id);

  const buildHeaders = useCallback(() => {
    const token = sessionStorage.getItem('authToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }, []);

  const loadCompanies = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/companies`, { headers: buildHeaders() });
      if (!response.ok) {
        throw new Error(text.companiesLoadFailed);
      }
      const payload = await response.json();
      const data = Array.isArray(payload) ? payload : [];
      setCompanies(data);
      if (!selectedCompanyId && data.length > 0) {
        setSelectedCompanyId(String(data[0].id));
      }
    } catch (caught) {
      setListError(caught instanceof Error ? caught.message : text.companiesLoadFailedDetail);
    }
  }, [buildHeaders, selectedCompanyId, text.companiesLoadFailed, text.companiesLoadFailedDetail]);

  const loadPeriods = useCallback(async (companyId) => {
    setLoading(true);
    setListError('');
    try {
      const params = new URLSearchParams();
      if (companyId) {
        params.set('companyId', String(companyId));
      }
      const query = params.toString();
      const response = await fetch(`${API_BASE_URL}/api/periods${query ? `?${query}` : ''}`, {
        headers: buildHeaders()
      });
      if (!response.ok) {
        throw new Error(text.periodsLoadFailed);
      }
      setPeriods(await response.json());
    } catch (caught) {
      setListError(caught instanceof Error ? caught.message : text.periodsLoadFailedDetail);
    } finally {
      setLoading(false);
    }
  }, [buildHeaders, text.periodsLoadFailed, text.periodsLoadFailedDetail]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    loadPeriods(selectedCompanyId);
  }, [loadPeriods, selectedCompanyId]);

  const resetForm = useCallback(() => {
    setFormState(createFormState(selectedCompanyId));
    setFormError('');
  }, [selectedCompanyId]);

  const openCreateModal = () => {
    resetForm();
    setSuccess('');
    setIsModalOpen(true);
  };

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    resetForm();
  }, [resetForm]);

  const codePreview = useMemo(
    () => computeCodePreview(formState.periodType, formState.startDate, formState.endDate),
    [formState.endDate, formState.periodType, formState.startDate]
  );

  const suggestedLabel = useMemo(
    () => computeDefaultLabel(formState.periodType, formState.startDate, formState.endDate),
    [formState.endDate, formState.periodType, formState.startDate]
  );

  const preparePayload = useMemo(() => {
    return () => {
      const payload = {
        companyId: Number(formState.companyId),
        periodType: formState.periodType,
        startDate: formState.startDate,
        endDate: formState.endDate
      };

      const label = formState.label.trim() || suggestedLabel;
      if (label) {
        payload.label = label;
      }

      if (isEditMode && formState.status) {
        payload.status = formState.status;
      }

      return payload;
    };
  }, [formState.companyId, formState.endDate, formState.label, formState.periodType, formState.startDate, formState.status, isEditMode, suggestedLabel]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFormError('');
    setSuccess('');

    const payload = preparePayload();
    if (!payload.companyId || !payload.periodType || !payload.startDate || !payload.endDate) {
      setFormError(t('requiredFields'));
      setSaving(false);
      return;
    }
    if (payload.startDate > payload.endDate) {
      setFormError(t('invalidDateRange'));
      setSaving(false);
      return;
    }

    const method = isEditMode ? 'PUT' : 'POST';
    const url = `${API_BASE_URL}/api/periods${isEditMode ? `/${formState.id}` : ''}`;

    try {
      const response = await fetch(url, {
        method,
        headers: buildHeaders(),
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        if (body?.code === 'PACKAGE_LIMIT_EXCEEDED' || body?.code === 'PACKAGE_FEATURE_DISABLED') {
          throw new Error(body.message);
        }
        throw new Error(body?.message || body?.detail || t('operationFailed'));
      }
      setSuccess(isEditMode ? t('periodUpdated') : t('periodCreated'));
      closeModal();
      await loadPeriods(selectedCompanyId);
    } catch (caught) {
      setFormError(caught instanceof Error ? caught.message : t('operationFailedDetail'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (period) => {
    setFormState({
      id: period.id,
      companyId: String(period.companyId || selectedCompanyId || ''),
      periodType: period.periodType || 'YEARLY',
      label: period.label || period.description || '',
      startDate: period.startDate || '',
      endDate: period.endDate || '',
      status: period.status || 'OPEN'
    });
    setSuccess('');
    setFormError('');
    setIsModalOpen(true);
  };

  const handleDelete = async (periodId) => {
    const confirmed = window.confirm(t('deleteConfirm'));
    if (!confirmed) {
      return;
    }

    setSaving(true);
    setListError('');
    setFormError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/periods/${periodId}`, {
        method: 'DELETE',
        headers: buildHeaders()
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || body?.detail || t('deleteFailed'));
      }
      setSuccess(t('periodDeleted'));
      if (formState.id === periodId) {
        closeModal();
      }
      await loadPeriods(selectedCompanyId);
    } catch (caught) {
      setListError(caught instanceof Error ? caught.message : t('deleteFailedDetail'));
    } finally {
      setSaving(false);
    }
  };

  const periodTypeLabel = (value) => ({
    MONTHLY: t('periodMonthly'),
    QUARTERLY: t('periodQuarterly'),
    SEMI_ANNUAL: t('periodSemiAnnual'),
    YEARLY: t('periodYearly'),
    CUSTOM: t('periodCustom')
  }[value] || value || '-');

  const statusLabel = (value) => ({
    OPEN: t('statusOpen'),
    LOCKED: t('statusLocked'),
    CLOSED: t('statusClosed')
  }[value] || value || '-');

  return (
    <div className="card periods-card">
      <PageHeader
        title={t('pageTitle')}
        description={t('pageDescription')}
        actions={
          <button type="button" className="btn-secondary" onClick={openCreateModal}>
            {t('newPeriod')}
          </button>
        }
      />
      {success && <div className="alert alert-success">{success}</div>}

      <div className="periods-grid">
        <section className="periods-table-panel">
          <div className="form-field" style={{ marginBottom: '1rem', maxWidth: '340px' }}>
            <label htmlFor="period-list-company">{t('company')}</label>
            <select
              id="period-list-company"
              value={selectedCompanyId}
              onChange={(event) => setSelectedCompanyId(event.target.value)}
              disabled={companies.length === 0}
            >
              {companies.length === 0 ? (
                <option value="">{t('companyNotFound')}</option>
              ) : (
                companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="table-wrapper">
            <table className="periods-table">
              <thead>
                <tr>
                  <th>{t('code')}</th>
                  <th>{t('label')}</th>
                  <th>{t('type')}</th>
                  <th>{t('start')}</th>
                  <th>{t('end')}</th>
                  <th>{t('status')}</th>
                  <th>{t('action')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="table-placeholder">
                      {t('loading')}
                    </td>
                  </tr>
                ) : listError ? (
                  <tr>
                    <td colSpan={7} className="table-placeholder">
                      {listError}
                    </td>
                  </tr>
                ) : periods.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="table-placeholder">
                      {t('noRecords')}
                    </td>
                  </tr>
                ) : (
                  periods.map((period) => (
                    <tr key={period.id}>
                      <td>{period.code}</td>
                      <td>{period.label || period.description || '-'}</td>
                      <td>{periodTypeLabel(period.periodType)}</td>
                      <td>{formatDisplayDate(period.startDate, language)}</td>
                      <td>{formatDisplayDate(period.endDate, language)}</td>
                      <td>
                        <span className={period.status === 'OPEN' ? 'badge badge-active' : 'badge'}>{statusLabel(period.status)}</span>
                      </td>
                      <td className="action-cell">
                        <button
                          type="button"
                          className="action-icon-btn action-icon-btn-edit"
                          onClick={() => handleEdit(period)}
                          aria-label={t('edit')}
                          title={t('edit')}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M3 17.25V21h3.75L18.81 8.94l-3.75-3.75L3 17.25Zm17.71-10.04a1.003 1.003 0 0 0 0-1.42L18.2 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 2.01-1.66Z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="action-icon-btn action-icon-btn-delete"
                          onClick={() => handleDelete(period.id)}
                          aria-label={t('delete')}
                          title={t('delete')}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M6 7h12l-1 14H7L6 7Zm3-3h6l1 2h4v2H4V6h4l1-2Z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal} title={isEditMode ? t('editPeriodTitle') : t('newPeriodTitle')} width="560px">
        <section>
          {formError && <div className="alert alert-error">{formError}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-field">
              <label htmlFor="companyId">{t('company')}</label>
              <select
                id="companyId"
                name="companyId"
                value={formState.companyId}
                onChange={(event) => setFormState((current) => ({ ...current, companyId: event.target.value }))}
                required
              >
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="periodType">{t('periodType')}</label>
              <select
                id="periodType"
                name="periodType"
                value={formState.periodType}
                onChange={(event) => setFormState((current) => ({ ...current, periodType: event.target.value }))}
                required
              >
                {PERIOD_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {periodTypeLabel(type)}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="startDate">{t('startDate')}</label>
              <input
                id="startDate"
                name="startDate"
                type="date"
                value={formState.startDate}
                onChange={(event) => setFormState((current) => ({ ...current, startDate: event.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="endDate">{t('endDate')}</label>
              <input
                id="endDate"
                name="endDate"
                type="date"
                value={formState.endDate}
                onChange={(event) => setFormState((current) => ({ ...current, endDate: event.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="label">{t('periodLabel')}</label>
              <input
                id="label"
                name="label"
                value={formState.label}
                onChange={(event) => setFormState((current) => ({ ...current, label: event.target.value }))}
                placeholder={suggestedLabel || t('periodLabelPlaceholder')}
              />
            </div>
            <div className="form-field">
              <label htmlFor="codePreview">{t('codeGenerated')}</label>
              <input id="codePreview" name="codePreview" value={codePreview} readOnly />
            </div>
            {isEditMode && (
              <div className="form-field">
                <label htmlFor="status">{t('status')}</label>
                <select
                  id="status"
                  name="status"
                  value={formState.status}
                  onChange={(event) => setFormState((current) => ({ ...current, status: event.target.value }))}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {statusLabel(option)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-footer">
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? t('saving') : isEditMode ? t('update') : t('save')}
              </button>
              <button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>
                {t('cancel')}
              </button>
            </div>
          </form>
        </section>
      </Modal>
    </div>
  );
}

export default PeriodsPage;
